import {
	createEligibilityCache,
	createWithEligibilityCache,
	type CacheController,
} from '@atlassian/cross-join-nudges/services/caching';

import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys as localStorageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

type EligibilityCacheKey =
	| 'user_already_has_jira'
	| 'user_already_has_jpd'
	| 'user_already_has_loom'
	| 'user_already_has_active_loom_license'
	| 'jira_is_too_new'
	| 'jpd_is_too_new'
	| 'user_is_too_new'
	| 'user_cannot_request_jira_access'
	| 'user_cannot_request_jpd_access'
	| 'user_cannot_request_loom_access'
	| 'site_is_opted_out'
	| 'has_seen_jpd'
	| 'has_seen_jira'
	| 'local_storage_enabled'
	| 'has_seen_nudge_in_last_7_days'
	| 'loom_site_is_more_than_7_days_old';

export type LoomEventCacheValue = {
	eligible: boolean;
	ineligibilityReason?: string;
};

export type LoomEventCacheKey = 'cjn-conf-loom';

export type CrossJoinTargetProduct = 'loom';

const cjnEligibilityCacheController: CacheController<EligibilityCacheKey> = {
	get: () => localStorage.getItem(localStorageKeys.CROSS_JOIN_NUDGES_ELIGIBILITY_KEY),
	set: (value) => localStorage.setItem(localStorageKeys.CROSS_JOIN_NUDGES_ELIGIBILITY_KEY, value),
	delete: () => localStorage.removeItem(localStorageKeys.CROSS_JOIN_NUDGES_ELIGIBILITY_KEY),
};

export const cjnEligibilityCacheHandler: {
	delete: (key: EligibilityCacheKey) => Promise<void>;
	deleteAll: () => Promise<void>;
	get: (key: EligibilityCacheKey) => Promise<boolean | void>;
	set: (key: EligibilityCacheKey, value: boolean, ttlDays?: number) => Promise<void>;
} = createEligibilityCache(cjnEligibilityCacheController);
export const withEligibilityCache: <A extends unknown[]>(
	key: EligibilityCacheKey,
	eligibilityCheck: (...args: A) => Promise<boolean>,
	daysToLive?: number,
) => (...args: A) => Promise<boolean> = createWithEligibilityCache<EligibilityCacheKey>(
	cjnEligibilityCacheHandler,
);

export const cjnLoomEventCacheController = {
	get: (key: LoomEventCacheKey | string): LoomEventCacheValue | undefined => {
		const storedValue = localStorage.getItem(
			localStorageKeys.CROSS_JOIN_NUDGES_LOOM_EVENT_CACHE_KEY,
		);
		if (!storedValue) {
			return undefined;
		}
		const storedValueObject = JSON.parse(storedValue);
		return storedValueObject[key];
	},
	set: (key: LoomEventCacheKey | string, value: LoomEventCacheValue): void => {
		const storedValue = JSON.parse(
			localStorage.getItem(localStorageKeys.CROSS_JOIN_NUDGES_LOOM_EVENT_CACHE_KEY) ?? '{}',
		);
		storedValue[key] = value;
		localStorage.setItem(
			localStorageKeys.CROSS_JOIN_NUDGES_LOOM_EVENT_CACHE_KEY,
			JSON.stringify(storedValue),
		);
	},
	delete: (key: LoomEventCacheKey | string): void => {
		const storedValue =
			localStorage.getItem(localStorageKeys.CROSS_JOIN_NUDGES_LOOM_EVENT_CACHE_KEY) ?? {};
		delete storedValue[key];
		localStorage.setItem(
			localStorageKeys.CROSS_JOIN_NUDGES_EVENT_CACHE_KEY,
			JSON.stringify(storedValue),
		);
	},
};

export const isCacheEnabled = async (): Promise<boolean> => {
	await cjnEligibilityCacheHandler.set('local_storage_enabled', true);
	const value = await cjnEligibilityCacheHandler.get('local_storage_enabled');
	return value === true;
};

export const setHasSeenNudgeInLast7Days = async (): Promise<void> => {
	try {
		// overwrite the key even if it exists
		await cjnEligibilityCacheHandler.set('has_seen_nudge_in_last_7_days', true);
	} catch (e) {
		// cache set fail, do nothing
	}
};

export const getHasSeenNudgeInLast7Days = async (): Promise<boolean> => {
	try {
		return !!(await cjnEligibilityCacheHandler.get('has_seen_nudge_in_last_7_days'));
	} catch (e) {
		// cache get fail, return false
	}
	return false;
};
